import { signedAmount } from './../../../../shared/transaction-balance.js';
import { columnDisplay, formatDate } from '../../../../shared/format-date.js';
import { AccountLink } from '../navigation/account-link.jsx';
import { ActionMenu } from '../interactive/action-menu.jsx';
import { Amount } from './amount.jsx';
import { ClickableRow } from '../interactive/clickable-row.jsx';
import { Link } from '../navigation/link.jsx';
import { SecondaryLine } from './secondary-line.jsx';
import { TagPills } from './tag-pills.jsx';
import { i18n } from '../../locale/i18n.js';

/**
 * @param {Object} row
 * @returns {{ pairedAccount: string|null, pairedAmt: number|null, pairedType: string|null, pairedCurSym: string|undefined, pairedCurDp: number|undefined }}
 */
function extractPaired(row) {
	return {
		pairedAccount: row.pairedAccountName || null,
		pairedAmt: row.pairedAmount ?? row.estimatedAmount ?? null,
		pairedType: row.pairedType || row.transactionType || null,
		pairedCurSym: row.pairedCurrencySymbol || row.destCurrencySymbol || row.currencySymbol,
		pairedCurDp: row.pairedCurrencyDecimalPlaces ?? row.destCurrencyDecimalPlaces ?? row.currencyDecimalPlaces,
	};
}

/**
 * Unified pair row renderer for transfer pairs, pending pairs, and
 * schedule pairs. Reads `paired*` fields from the row directly.
 *
 * @param {Object} row
 * @param {Array<{ key: string, label: string, type: string, align?: string, getValue: (c: any) => any, getDisplayValue?: (c: any) => string, linkTo?: (row: any) => string|undefined, priority?: number, filterType?: string }>} columns
 * @param {number} maxPriority
 * @param {Array<{ label: string, onClick: () => void, to?: string, danger?: boolean }>} actions
 * @param {{ rowLink?: (row: any) => string | undefined, navigate?: (path: string) => void, dateFormat?: string }} [nav]
 * @returns {import('react').ReactElement | null}
 */
/**
 * @param {string} key
 * @returns {string}
 */
function getStatusLabel(key) {
	const labels = { pending: i18n.t('status.pending'), confirmed: i18n.t('status.confirmed'), cancelled: i18n.t('status.cancelled'), skipped: i18n.t('status.skipped') };
	return labels[/** @type {keyof typeof labels} */ (key)] || key;
}

export function DoubleRow(row, columns, maxPriority, actions, { rowLink, navigate, dateFormat = 'iso' } = {}) {
	const { pairedAccount, pairedAmt, pairedType, pairedCurSym, pairedCurDp } = extractPaired(row);

	if (!pairedAccount && !pairedAmt) { return null; }

	const pendingAmt = row.amount ?? row.confirmedAmount ?? row.estimatedAmount ?? 0;
	const signedAmt = signedAmount(Number(pendingAmt), row.transactionType || row.type || '');
	const arrowAmt = row.pairingSide === 'dest' ? -signedAmt : signedAmt;
	const rowPath = rowLink ? rowLink(row) : undefined;

	return (
		<ClickableRow
			key={row.id ?? row.code}
			className="double-row"
			to={rowPath}
			navigate={navigate}
		>
			{columns.map((col) => {
				const colPriority = col.priority ?? 1;
				if (colPriority > maxPriority) {return null;}

				let primary;
				if (col.getTags) {
					primary = <TagPills names={col.getTags(row)} />;
				} else if (col.type === 'currency' && col.getCurrency) {
					const cur = col.getCurrency(row);
					if (cur) {
						primary = <Amount masked value={cur.value} decimalPlaces={cur.decimalPlaces} symbol={cur.symbol} />;
					}
				}
				if (!primary) {
					primary = columnDisplay(col, row, dateFormat);
				}

				const isNumeric = col.type === 'number' || col.type === 'currency';
				const alignCls = col.align || (isNumeric ? 'right' : '');
				const wrapCls = col.getTags ? '' : (col.type === 'text' ? 'truncate' : 'nowrap');
				const tdCls = [alignCls, wrapCls].filter(Boolean).join(' ');

				const secondary = getSecondary(col.key, row, { pairedAccount, pairedAmt, pairedType, pairedCurSym, pairedCurDp, dateFormat });

				return (
					<td key={col.key} className={tdCls}>
						<div className="primary-line">
							{col.linkTo && col.linkTo(row)
								? <Link to={col.linkTo(row)} className="link">{primary}</Link>
								: primary}
						</div>
						{secondary !== null && renderSecondary(col.key, secondary, arrowAmt, row.pairedAccountId)}
					</td>
				);
			})}
			{columns.some((c) => (c.priority ?? 1) <= maxPriority) && (
				<td className="actions"><ActionMenu items={actions} /></td>
			)}
		</ClickableRow>
	);
}

/**
 * @param {string} colKey
 * @param {Record<string, unknown>} row
 * @param {{ pairedAccount: string|null, pairedAmt: number|null, pairedType: string|null, pairedCurSym: string|undefined, pairedCurDp: number|undefined, dateFormat: string }} paired
 * @returns {import('react').ReactNode}
 */
function getSecondary(colKey, row, { pairedAccount, pairedAmt, pairedType, pairedCurSym, pairedCurDp, dateFormat }) {
	switch (colKey) {
		case 'date':
		case 'nextDate':
			if (row.pairedDate && String(row.pairedDate) !== String(row.date) && String(row.pairedDate) !== String(row.nextDate)) {
				return formatDate(String(row.pairedDate), dateFormat);
			}
			return null;

		case 'type':
		case 'transactionType':
		case 'displayAccount':
		case 'accountName':
			if (!pairedAccount) {return null;}
			return pairedAccount;

		case 'amount':
		case 'displayAmount': {
			if (pairedAmt === null) {return null;}
			const pairedVal = row.pairedSide === 'source' ? signedAmount(Number(pairedAmt), pairedType || '') : pairedAmt;
			return { amount: pairedVal, symbol: pairedCurSym, decimalPlaces: pairedCurDp };
		}

		case 'status': {
			const displayStatus = row.pairedDisplayStatus;
			const terminal = ['confirmed', 'cancelled', 'skipped'].includes(displayStatus);
			const pairedStatus = terminal ? getStatusLabel(displayStatus) : displayStatus ?? getStatusLabel(row.pairedStatus);
			return pairedStatus || null;
		}

		default:
			return null;
	}
}

/**
 * @param {string} colKey
 * @param {import('react').ReactNode} secondary
 * @param {number} signedAmt
 * @param {string} pairAcctId
 */
function renderSecondary(colKey, secondary, signedAmt, pairAcctId) {
	const isAccountCol = colKey === 'displayAccount' || colKey === 'accountName' || colKey === 'type' || colKey === 'transactionType';

	if (isAccountCol && typeof secondary === 'string' && secondary) {
		return (
			<SecondaryLine amount={signedAmt}>
				<AccountLink id={pairAcctId} name={secondary} />
			</SecondaryLine>
		);
	}

	if ((colKey === 'amount' || colKey === 'displayAmount') && secondary && typeof secondary === 'object' && 'amount' in secondary) {
		return (
			<SecondaryLine amount={signedAmt} className="value">
				<Amount masked value={secondary.amount} decimalPlaces={secondary.decimalPlaces} symbol={secondary.symbol} />
			</SecondaryLine>
		);
	}

	if (typeof secondary === 'string' || typeof secondary === 'number') {
		return <SecondaryLine amount={signedAmt}>{String(secondary)}</SecondaryLine>;
	}

	return null;
}
